import { action } from '../tools/reduxTools';
import { ValidationResponse, IFormData, ITypesWithStatuses, IGetSingleLastValidation, EStatusesTypes } from '../entities/IValidation';
import { ISnapshotData } from '../entities/IClusters';
import * as actionTypes from './actionTypes/approvalTypes';
import { CLEAR_APPROVAL_OVERVIEW_INFORMATIONS } from './actionTypes/approvalTypes';
import { setHasActiveApprovalRequests } from './contextActions';
import { EDatatableKeys, EDatatableScope, IDatatableSearchPayload } from './datatableActions';
import { ELcid } from '../entities/ILanguage';
import { ESortTypes } from '../entities/AssetManagement/global';

const USER_ACTIVE_VALIDATIONS_DATATABLE_PAGE_SIZE = 40;

/** Same filter/sort as MyApprovals "todo" — validations where the user must decide now. */
function buildUserActiveValidationRequestsSearchPayload(lcid: ELcid): IDatatableSearchPayload {
    return {
        page: 0,
        size: USER_ACTIVE_VALIDATIONS_DATATABLE_PAGE_SIZE,
        lcid,
        filters: [
            {
                field: 'decisionToMakeNow',
                filterPath: 'ValidationRequest/decisionToMakeNow/PrincipalsToMakeDecisionNow',
                fieldValueType: 'Boolean',
                value: { value: 'true' }
            }
        ],
        orderBy: [
            {
                field: 'createdAt',
                sortDirection: ESortTypes.Ascending,
                sortPath: 'ValidationRequest/createdAt'
            }
        ]
    };
}

export const changeValidationStatus = (resourceId: string, validationRequestId: string, validatorRowId: string, status: EStatusesTypes, principalId?: string) => action<Promise<boolean>>(async (dispatch, getState, http) => {
    if (!validatorRowId) {
        throw new Error('validatorRowId is required to change validation status');
    }
    dispatch({
        type: actionTypes.LOCK_APPROVAL_OVERVIEW
    });
    const principalQuery = principalId ? `?principalId=${principalId}` : '';
    const endpoint = `/resources/${resourceId}/validations/${validationRequestId}/validators/${validatorRowId}/setStatus/${status}${principalQuery}`;
    const result = await http.validationsApiPatch(endpoint);

    const { profile } = getState();
    const userId = profile?.currentUserProfile?.id;
    if (userId) {
        const refreshApprovalsBadge = () => dispatch(checkUserHasActiveApprovalRequests(userId)).catch(() => {});
        refreshApprovalsBadge();
        globalThis.setTimeout(refreshApprovalsBadge, 1200);
    }

    return result;
});

export const getSingleValidation = (resourceId: string, validationRequestId: string, onlyData?: boolean) => action<Promise<ValidationResponse>>(async (dispatch, getState, http) => {
    return http.validationsApiGet(`/resources/${resourceId}/validations/${validationRequestId}`).then((response: ValidationResponse) => {
        if (!onlyData) {
            dispatch({
                type: actionTypes.SET_APPROVAL_OVERVIEW_INFORMATIONS,
                singleValidation: response
            });
        }

        return response;
    });
});

export const createNewValidation = (body: IFormData) => action<Promise<IFormData>>(async (dispatch, getState, http) => {
    return http.validationsApiPost(`/validations`, body);
});

export const updateValidation = (body: IFormData, validationId: string) => action<Promise<IFormData>>(async (dispatch, getState, http) => {
    return http.validationsApiPut(`/validations/${validationId}`, body);
});

export const getSnapshotImage = (validationRequestId: string) => action<Promise<{ link: string, name: string }>>(async (dispatch, getState, http) => {
    return http.validationsApiGet(`/validations/${validationRequestId}/snapshot`);
});

export const getApprovalSnapshot = (organizationId: string, clusterId: string) => action<Promise<ISnapshotData>>(async (dispatch, getState, http) => {
    const body = {
        organizationId,
        clusterId
    };

    return http.validationsApiPost(`/validations/new-snapshot`, body);
});

export const getSingleLastValidation = (resourceId: string, type: string) => action<Promise<IGetSingleLastValidation>>(async (dispatch, getState, http) => {
    const body = {
        type
    };
    return http.validationsApiPost(`/resources/${resourceId}/validations?page=0&size=1`, body).then((response: IGetSingleLastValidation) => {
        dispatch({
            type: actionTypes.UNLOCK_APPROVAL_OVERVIEW
        });
        return response;
    });
});

export const getTypesWithStatuses = () => action<Promise<ITypesWithStatuses[]>>(async (dispatch, getState, http) => {
    return http.validationsApiGet(`/validations/typesWithStatuses`);
});

export const getUserAllValidations = (userId: string) => action<Promise<any[]>>(async (dispatch, getState, http) => {
    return http.clustersApiPost(`/validations?userId=${userId}`, {
        Query: ''
    });
});

export const getOrganizationAllValidations = (organizationId: string) => action<Promise<any[]>>(async (dispatch, getState, http) => {
    return http.clustersApiPost(`/validations?organizationId=${organizationId}`, {});
});

export const disableValidationRequest = (resourceId: string, validationRequestId: string) => action<Promise<boolean>>(async (dispatch, getState, http) => {
    return http.validationsApiPost(`/resources/${resourceId}/validations/${validationRequestId}/disable`, {});
});

export const clearApprovalInfo = () => action(async (dispatch) => {
    dispatch({ type: CLEAR_APPROVAL_OVERVIEW_INFORMATIONS });
});

export const checkUserHasActiveApprovalRequests = (userId: string) => action<Promise<boolean>>(async (dispatch, getState, http) => {
    const { language } = getState();
    const lcid = language.userLanguage || ELcid.Fr;
    const payload = buildUserActiveValidationRequestsSearchPayload(lcid);
    const { count } = await http.commonApiPost(
        `/data-table/search/${EDatatableScope.PROFILES}/${userId}/${EDatatableKeys.VALIDATION_REQUEST}?lcid=${lcid}`,
        payload
    );
    dispatch(setHasActiveApprovalRequests(count > 0));
    return count > 0;
});